type StorageStrategy = "local" | "session";

export type StoredAuthCredentials = {
  username: string;
  password: string;
  remember: boolean;
};

const CREDENTIALS_KEY = "parking-box:auth-credentials";
const isBrowser = typeof window !== "undefined";

const getStorage = (strategy: StorageStrategy): Storage | null => {
  if (!isBrowser) {
    return null;
  }

  try {
    return strategy === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

const getStorages = (): Storage[] => {
  const storages: Storage[] = [];
  const session = getStorage("session");
  const local = getStorage("local");

  if (session) {
    storages.push(session);
  }
  if (local) {
    storages.push(local);
  }

  return storages;
};

const safeParse = (payload: string | null): StoredAuthCredentials | null => {
  if (!payload) {
    return null;
  }

  try {
    const parsed = JSON.parse(payload);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const record = parsed as Record<string, unknown>;
    const username = typeof record.username === "string" ? record.username.trim() : "";
    const password = typeof record.password === "string" ? record.password : "";
    const remember = typeof record.remember === "boolean" ? record.remember : false;

    if (!username || !password) {
      return null;
    }

    return {
      username,
      password,
      remember,
    };
  } catch {
    return null;
  }
};

const safeStringify = (credentials: StoredAuthCredentials): string => {
  try {
    return JSON.stringify(credentials);
  } catch {
    return "{}";
  }
};

export const persistAuthCredentials = (credentials: StoredAuthCredentials) => {
  if (!isBrowser) {
    return;
  }

  const normalized: StoredAuthCredentials = {
    username: credentials.username.trim(),
    password: credentials.password,
    remember: credentials.remember,
  };

  if (!normalized.username || !normalized.password) {
    clearStoredAuthCredentials();
    return;
  }

  const serialized = safeStringify(normalized);
  const targetStrategy: StorageStrategy = normalized.remember ? "local" : "session";
  const target = getStorage(targetStrategy) ?? getStorage("local") ?? getStorage("session");

  if (!target) {
    return;
  }

  const storages = getStorages();
  for (const storage of storages) {
    if (storage === target) {
      continue;
    }
    try {
      storage.removeItem(CREDENTIALS_KEY);
    } catch {
      // ignore storage errors
    }
  }

  try {
    target.setItem(CREDENTIALS_KEY, serialized);
  } catch {
    // ignore storage errors
  }
};

export const readStoredAuthCredentials = (): StoredAuthCredentials | null => {
  if (!isBrowser) {
    return null;
  }

  const storages = getStorages();
  for (const storage of storages) {
    let payload: string | null = null;
    try {
      payload = storage.getItem(CREDENTIALS_KEY);
    } catch {
      payload = null;
    }

    const parsed = safeParse(payload);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

export const clearStoredAuthCredentials = () => {
  if (!isBrowser) {
    return;
  }

  const storages = getStorages();
  for (const storage of storages) {
    try {
      storage.removeItem(CREDENTIALS_KEY);
    } catch {
      // ignore storage errors
    }
  }
};
